import {Component, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges, WritableSignal} from '@angular/core';
import {CCDEvent, CCDEventType, ContentCategoryDistributionResponse, Row} from './types/types';
import {AgGridAngular} from 'ag-grid-angular';
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  ColDef,
  ColumnHeaderMouseLeaveEvent,
  ColumnHeaderMouseOverEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  themeQuartz
} from 'ag-grid-community';
import {ValueCellComponent} from './components/value-cell/value-cell.component';
import {CategoryCellComponent} from './components/category-cell/category-cell.component';
import {HeaderCellComponent} from './components/header-cell/header-cell.component';
import {SelectBrandItem} from '../../../services/dashboard.service';
import {NgClass, NgIf} from '@angular/common';
import {SortDirection} from '../../../utils/sort-state';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {NoWidgetDataComponent} from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { ModuleRegistry } from 'ag-grid-community';
import { ExternalFilterModule } from 'ag-grid-community';
import { ClientSideRowModelApiModule, TooltipModule, RowApiModule, ColumnAutoSizeModule, PaginationModule } from 'ag-grid-community';

@Component({
  selector: 'app-content-category-distribution',
  imports: [
    AgGridAngular,
    NgIf,
    NgClass,
    LoaderComponent,
    NoWidgetDataComponent
  ],
  templateUrl: './content-category-distribution.component.html',
  styleUrl: './content-category-distribution.component.scss'
})
export class ContentCategoryDistributionComponent implements OnChanges {
  @Input({required: true}) myBrand!: SelectBrandItem;
  @Input({required: true}) myCompetitors!: SelectBrandItem[];
  @Input() data?: ContentCategoryDistributionResponse;
  @Input() brandColorsMap?: Record<string, string>;
  @Input() loading = false;
  @Input() showFullListButton = true;
  @Input() isAdType = false;
  @Output() collapseExpand = new EventEmitter<"collapse" | "expand">();

  constructor() {
    ModuleRegistry.registerModules([
      TooltipModule,
      ExternalFilterModule,
      ClientSideRowModelApiModule,
      ColumnAutoSizeModule,
      RowApiModule,
      PaginationModule
    ]);
  }

  protected events = new EventEmitter<CCDEvent>();
  protected showingTopFive = true;
  protected colDef?: ColDef[];
  protected rowData?: Row[];
  protected buttonText: string = '+ See full list'
  protected isColumnsHover: WritableSignal<boolean> = signal(false);
  protected get categoriesColumnWidth(): number { return this.isAdType ? 180 : 230; }

  private gridApi?: GridApi;

  protected theme = themeQuartz.withParams({
    pinnedColumnBorder: {style: 'solid', width: 1, color: '#9CA3AF'},
    rowHeight: 28,
    rowBorder: {style: 'none'},
    selectCellBorder: {style: 'none'},
    cellHorizontalPadding: 0,
    headerBackgroundColor: 'transparent',
    wrapperBorder: {style: 'none'},
    headerRowBorder: {style: 'none'},
  });

  protected defaultColDef: ColDef = {
    resizable: false,
  };

  protected gridOptions: GridOptions = {
    suppressMovableColumns: true,
    pagination: true,
    paginationPageSize: 5,
    tooltipShowMode: "whenTruncated",
    suppressRowDrag: true,
    rowDragManaged: false,
    suppressCellFocus: true,
    suppressRowHoverHighlight: true,
    enableBrowserTooltips: false,
    tooltipShowDelay: 300,
    tooltipTrigger: "hover",
    domLayout: 'autoHeight',
    suppressMultiSort: true,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 150,
    },
    onCellMouseOver: (event: CellMouseOverEvent) => {
      this.isColumnsHover.set(true);
      this.events.emit({
        type: CCDEventType.HOVER_IN,
        colId: event.column.getId() || '',
      })
    },
    onColumnHeaderMouseOver: (event: ColumnHeaderMouseOverEvent) => {
      this.isColumnsHover.set(true)
      this.events.emit({
        type: CCDEventType.HOVER_IN,
        colId: event.column.getId() || ''
      })
    },
    onColumnHeaderMouseLeave: (event: ColumnHeaderMouseLeaveEvent) => {
      this.isColumnsHover.set(false);
      this.events.emit({
        type: CCDEventType.HOVER_OUT,
        colId: event.column.getId() || ''
      })
    },
    onCellMouseOut: (event: CellMouseOutEvent) => {
      this.isColumnsHover.set(false);
      this.events.emit({
        type: CCDEventType.HOVER_OUT,
        colId: event.column.getId() || ''
      })
    },
    onGridReady: (event: GridReadyEvent<any>)=> {
      this.gridApi = event.api;
      this.sizeToFit();
    },
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data && (changes['data'] || changes['myBrand'] || changes['myCompetitors'])) {
      this.orderData();
      this.setRowData();
      this.setColDef();
      this.sizeToFit();
    }
  }

  protected toggleFullList(): void {
    this.showingTopFive = !this.showingTopFive;
    this.buttonText =  this.showingTopFive ? '+ See full list' : '- Collapse full list';
    this.collapseExpand.emit(this.showingTopFive ? "collapse" : "expand");
    this.gridApi?.setGridOption('pagination', this.showingTopFive)
  }


  private orderData(): void {
    if (!this.data) return;
    let {myBrand, myCompetitors} = this.data;
    myCompetitors = myCompetitors.sort((compA, compB) => {
      const idxA = myCompetitors.findIndex(({brandKey})=> brandKey === compA.brandKey);
      const idxB = myCompetitors.findIndex(({brandKey})=> brandKey === compB.brandKey);
      return idxA - idxB
    });
    this.data = {myBrand, myCompetitors}
  }

  private setRowData(): void {
    if (!this.data) return;
    const {myBrand, myCompetitors} = this.data;
    const allBrands = [myBrand, ...myCompetitors];
    const brandsMap = allBrands.reduce((map, {brandKey, shares}) => {
      map[brandKey] = shares;
      return map
    }, {} as Record<string, Record<string, number>>);
    const categories = allBrands.flatMap(({shares}) => Object.keys(shares));
    const categoriesSet = new Set<string>(categories);
    this.rowData = Array.from(categoriesSet).map((category ,index) => {
      const row: Row = {category, index};
      Object.keys(brandsMap).forEach((brand) => row[brand] = brandsMap[brand]?.[category] || 0);
      return row;
    });
    this.gridApi?.setGridOption('rowData', this.rowData);
  }

  private setColDef(): void {
    if (!this.data) return;
    const {myBrand, myCompetitors} = this.data;
    const brandsDataMap = [this.myBrand, ...this.myCompetitors]
      .reduce((map, brand, index) => {
        map[brand.key] = {...brand,  index};
        return map
      }, {} as Record<string, Partial<SelectBrandItem> & {index: number}>);
    const allBrands = [myBrand, ...myCompetitors]
      .sort((a, b) => brandsDataMap[a.brandKey]?.index - brandsDataMap[b.brandKey]?.index);

    this.colDef = [
      {
        headerClass: 'hide-header',
        field: 'category',
        colId: 'category',
        pinned: 'left',
        tooltipField: 'category',
        sortable: false,
        width: this.categoriesColumnWidth,
        minWidth: this.categoriesColumnWidth,
        maxWidth: this.categoriesColumnWidth,
        cellRenderer: CategoryCellComponent,
        cellRendererParams: (params: any) => ({
          category: params.data?.category,
        })
      },
      ...allBrands.map(({brandKey}) => ({
        headerName: brandKey,
        field: brandKey,
        collId: brandKey,
        headerTooltip: brandsDataMap[brandKey]?.label,
        cellStyle: {border: 0},
        sortable: true,
        suppressHeaderMenuButton: false,
        headerComponent: HeaderCellComponent,
        autoHeaderHeight: true,
        headerComponentParams: (params: any) => ({
          brand: brandsDataMap[brandKey],
          events: this.events,
          isMyBrand: brandKey === this.myBrand?.key,
          defaultSort: brandKey === this.myBrand?.key ? SortDirection.desc : SortDirection.default,
        }),
        cellRenderer: ValueCellComponent,
        cellRendererParams: (params: any) => ({
          isColumnsHover: this.isColumnsHover,
          brandKey,
          events: this.events,
          color: this.brandColorsMap?.[brandKey] ?? 'transparent',
          value: params.data?.[brandKey]
        })
      }))
    ];
    this.gridApi?.setGridOption('columnDefs', this.colDef);
  }


  private sizeToFit(): void {
    this.gridApi?.sizeColumnsToFit({
      defaultMinWidth: 150,
      columnLimits: [
        {
          key: 'category',
          minWidth: this.categoriesColumnWidth,
          maxWidth: this.categoriesColumnWidth
        }
      ]
    })
  }

  protected readonly Object = Object;
  protected readonly Boolean = Boolean;
}
